// components/DashboardFilters.jsx
// ═══════════════════════════════════════════════════════════════════════════
// Real-time filters — dark luxury fintech, consistent with AdminHome
// ═══════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { TrendingUp, TrendingDown, Calendar, ChevronDown, RefreshCw } from 'lucide-react';
import { db } from '../firebase/config';
import { collection, getDocs, query, where } from 'firebase/firestore';
import FormatPrice from './FormatPrice';

// ─── Date ranges ──────────────────────────────────────────────────────────
const RANGES = [
  { key: 'today',     label: 'Today' },
  { key: 'yesterday', label: 'Yesterday' },
  { key: '7d',        label: '7 days' },
  { key: '30d',       label: '30 days' },
  { key: 'month',     label: 'This month' },
  { key: 'year',      label: 'This year' },
];

const getDateRange = (key) => {
  const now   = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  switch (key) {
    case 'today':
      return {
        start: today,
        end:   new Date(today.getTime() + 86399999),
      };
    case 'yesterday': {
      const yest = new Date(today); yest.setDate(yest.getDate() - 1);
      return { start: yest, end: new Date(yest.getTime() + 86399999) };
    }
    case '7d':
      return { start: new Date(today.getTime() - 6 * 86400000), end: new Date(today.getTime() + 86399999) };
    case '30d':
      return { start: new Date(today.getTime() - 29 * 86400000), end: new Date(today.getTime() + 86399999) };
    case 'month':
      return {
        start: new Date(now.getFullYear(), now.getMonth(), 1),
        end:   new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999),
      };
    case 'year':
      return {
        start: new Date(now.getFullYear(), 0, 1),
        end:   new Date(now.getFullYear(), 11, 31, 23, 59, 59, 999),
      };
    default:
      return { start: new Date(today.getTime() - 29 * 86400000), end: new Date(today.getTime() + 86399999) };
  }
};

// ─── Trend color ──────────────────────────────────────────────────────────
const trendColor = (pct) =>
  pct > 0 ? '#34d399' : pct < 0 ? '#f87171' : '#94a3b8';

// ─── Small trend pill ──────────────────────────────────────────────────────
const TrendPill = ({ pct }) => {
  const color = trendColor(pct);
  const Icon  = pct >= 0 ? TrendingUp : TrendingDown;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 3,
      fontSize: 10, fontWeight: 700, color,
      background: `${color}18`,
      border: `1px solid ${color}33`,
      borderRadius: 20, padding: '2px 7px',
    }}>
      <Icon size={9} />
      {pct > 0 ? '+' : ''}{pct.toFixed(1)}%
    </span>
  );
};

// ─── Compact metric card ──────────────────────────────────────────────────
const MetricTile = ({ label, value, prev, format = String, accent = '#f59e0b', loading }) => {
  const pct = prev > 0 ? ((value - prev) / prev) * 100 : null;
  return (
    <div style={{
      background: 'rgba(17,24,39,.85)',
      border: `1px solid ${accent}22`,
      borderRadius: 12,
      padding: '12px 14px',
      minWidth: 130,
      boxShadow: `0 0 20px ${accent}0a`,
    }}>
      <p style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: '.14em', color: '#475569', marginBottom: 6 }}>
        {label}
      </p>
      {loading ? (
        <div style={{ height: 22, width: 80, background: 'rgba(255,255,255,.06)', borderRadius: 6, animation: 'pulse 1.5s ease-in-out infinite' }} />
      ) : (
        <>
          <p style={{ fontSize: 18, fontWeight: 700, color: '#fff', lineHeight: 1, marginBottom: 5 }}>
            {format(value)}
          </p>
          {pct !== null && <TrendPill pct={pct} />}
          {pct === null && prev === 0 && (
            <span style={{ fontSize: 9, color: '#334155' }}>No previous data</span>
          )}
        </>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════
export const AdvancedFilters = ({ onFilterChange }) => {
  const [activeKey, setActiveKey]     = useState('7d');
  const [loading, setLoading]         = useState(false);
  const [metrics, setMetrics]         = useState(null);   // { current, previous }
  const [showCustom, setShowCustom]   = useState(false);
  const [custom, setCustom]           = useState({ start: '', end: '' });

  // ─── Fetch data for a range ─────────────────────────────────────────────
  const fetchRange = useCallback(async (start, end) => {
    // Firestore: orderDate is an ISO string, compare lexicographically
    const startISO = start.toISOString();
    const endISO   = end.toISOString();

    const snap = await getDocs(
      query(
        collection(db, 'orders'),
        where('orderDate', '>=', startISO),
        where('orderDate', '<=', endISO),
      )
    );

    let revenue = 0, orders = snap.size;
    const users = new Set();
    snap.docs.forEach(doc => {
      const d = doc.data();
      revenue += d.totalAmount || d.total || d.amount || 0;
      if (d.userId) users.add(d.userId);
    });
    return { revenue, orders, users: users.size, avgOrder: orders > 0 ? revenue / orders : 0 };
  }, []);

  // ─── Compute ─────────────────────────────────────────────────────────────
  const compute = useCallback(async (key, customStart, customEnd) => {
    setLoading(true);
    try {
      let curRange;
      if (key === 'custom' && customStart && customEnd) {
        curRange = { start: new Date(customStart), end: new Date(customEnd + 'T23:59:59') };
      } else {
        curRange = getDateRange(key);
      }

      // Previous period of same duration
      const duration = curRange.end - curRange.start;
      const prevRange = {
        start: new Date(curRange.start - duration - 1),
        end:   new Date(curRange.start - 1),
      };

      const [current, previous] = await Promise.all([
        fetchRange(curRange.start, curRange.end),
        fetchRange(prevRange.start, prevRange.end),
      ]);

      setMetrics({ current, previous });

      // Notify parent
      onFilterChange?.({
        dateRange: curRange,
        current,
        previous,
      });
    } catch (e) {
      console.error('DashboardFilters error:', e);
    } finally {
      setLoading(false);
    }
  }, [fetchRange, onFilterChange]);

  // ─── Trigger on range change ────────────────────────────────────────────
  useEffect(() => {
    if (activeKey !== 'custom') {
      compute(activeKey);
    }
  }, [activeKey, compute]);

  const handleCustomApply = () => {
    if (custom.start && custom.end) compute('custom', custom.start, custom.end);
  };

  // ─── Active range label ────────────────────────────────────────────────
  const activeLabel = activeKey === 'custom'
    ? (custom.start && custom.end ? `${custom.start} → ${custom.end}` : 'Custom')
    : RANGES.find(r => r.key === activeKey)?.label ?? '';

  return (
    <div style={{ marginBottom: 4 }}>

      {/* ── Filter bar ── */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6, marginBottom: 14 }}>
        
        {/* Calendar icon decoration */}
        <div style={{
          width: 28, height: 28, borderRadius: 8,
          background: 'rgba(245,158,11,.1)', border: '1px solid rgba(245,158,11,.25)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          <Calendar size={13} color="#f59e0b" />
        </div>

        {/* Range pills */}
        {RANGES.map(r => (
          <motion.button
            key={r.key}
            onClick={() => { setActiveKey(r.key); setShowCustom(false); }}
            whileTap={{ scale: 0.95 }}
            style={{
              padding: '5px 13px',
              borderRadius: 20,
              fontSize: 11,
              fontWeight: 600,
              cursor: 'pointer',
              border: 'none',
              transition: 'all .15s',
              background: activeKey === r.key
                ? 'rgba(245,158,11,.18)'
                : 'rgba(255,255,255,.05)',
              color: activeKey === r.key ? '#f59e0b' : '#64748b',
              boxShadow: activeKey === r.key
                ? 'inset 0 0 0 1px rgba(245,158,11,.35)'
                : 'inset 0 0 0 1px rgba(255,255,255,.06)',
            }}
          >
            {r.label}
          </motion.button>
        ))}

        {/* Custom button */}
        <motion.button
          onClick={() => { setShowCustom(v => !v); setActiveKey('custom'); }}
          whileTap={{ scale: 0.95 }}
          style={{
            padding: '5px 13px',
            borderRadius: 20,
            fontSize: 11,
            fontWeight: 600,
            cursor: 'pointer',
            border: 'none',
            display: 'flex', alignItems: 'center', gap: 5,
            background: activeKey === 'custom'
              ? 'rgba(245,158,11,.18)'
              : 'rgba(255,255,255,.05)',
            color: activeKey === 'custom' ? '#f59e0b' : '#64748b',
            boxShadow: activeKey === 'custom'
              ? 'inset 0 0 0 1px rgba(245,158,11,.35)'
              : 'inset 0 0 0 1px rgba(255,255,255,.06)',
          }}
        >
          Custom
          <ChevronDown size={11} style={{ transform: showCustom ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }} />
        </motion.button>

        {/* Loading spinner */}
        {loading && (
          <RefreshCw size={13} color="#f59e0b"
            style={{ animation: 'spin 0.8s linear infinite', flexShrink: 0 }} />
        )}
      </div>

      {/* ── Custom date picker ── */}
      <AnimatePresence>
        {showCustom && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.18 }}
            style={{ overflow: 'hidden', marginBottom: 12 }}
          >
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
              background: 'rgba(17,24,39,.85)',
              border: '1px solid rgba(255,255,255,.06)',
              borderRadius: 12, padding: '10px 14px',
            }}>
              <span style={{ fontSize: 10, color: '#475569', textTransform: 'uppercase', letterSpacing: '.1em' }}>From</span>
              <input
                type="date"
                value={custom.start}
                onChange={e => setCustom(p => ({ ...p, start: e.target.value }))}
                style={{
                  background: 'rgba(255,255,255,.04)', border: '1px solid rgba(255,255,255,.1)',
                  borderRadius: 8, padding: '5px 10px', color: '#e2e8f0', fontSize: 12,
                  colorScheme: 'dark',
                }}
              />
              <span style={{ fontSize: 10, color: '#475569', textTransform: 'uppercase', letterSpacing: '.1em' }}>To</span>
              <input
                type="date"
                value={custom.end}
                onChange={e => setCustom(p => ({ ...p, end: e.target.value }))}
                style={{
                  background: 'rgba(255,255,255,.04)', border: '1px solid rgba(255,255,255,.1)',
                  borderRadius: 8, padding: '5px 10px', color: '#e2e8f0', fontSize: 12,
                  colorScheme: 'dark',
                }}
              />
              <button
                onClick={handleCustomApply}
                disabled={!custom.start || !custom.end}
                style={{
                  padding: '5px 14px', borderRadius: 8, fontSize: 11, fontWeight: 700,
                  background: 'rgba(245,158,11,.2)', color: '#f59e0b',
                  border: '1px solid rgba(245,158,11,.35)', cursor: 'pointer',
                  opacity: !custom.start || !custom.end ? 0.45 : 1,
                }}
              >
                Apply
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Metric tiles ── */}
      <AnimatePresence mode="wait">
        {metrics && (
          <motion.div
            key={activeKey + (custom.start || '')}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.2 }}
            style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}
          >
            {/* Period label */}
            <div style={{
              width: '100%', display: 'flex', alignItems: 'center', gap: 8,
              fontSize: 10, color: '#334155', marginBottom: 2,
            }}>
              <div style={{ height: 1, flex: 1, background: 'rgba(255,255,255,.04)' }} />
              <span style={{ textTransform: 'uppercase', letterSpacing: '.12em', fontWeight: 600 }}>
                {activeLabel} — summary
              </span>
              <div style={{ height: 1, flex: 1, background: 'rgba(255,255,255,.04)' }} />
            </div>

            <MetricTile
              label="Orders"
              value={metrics.current.orders}
              prev={metrics.previous.orders}
              format={v => Math.floor(v).toString()}
              accent="#f59e0b"
              loading={loading}
            />
            <MetricTile
              label="Revenue (π)"
              value={metrics.current.revenue}
              prev={metrics.previous.revenue}
              format={FormatPrice}
              accent="#a78bfa"
              loading={loading}
            />
            <MetricTile
              label="Avg. order"
              value={metrics.current.avgOrder}
              prev={metrics.previous.avgOrder}
              format={FormatPrice}
              accent="#34d399"
              loading={loading}
            />
            <MetricTile
              label="Unique customers"
              value={metrics.current.users}
              prev={metrics.previous.users}
              format={v => Math.floor(v).toString()}
              accent="#38bdf8"
              loading={loading}
            />
          </motion.div>
        )}
      </AnimatePresence>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: .4; }
        }
        input[type="date"]::-webkit-calendar-picker-indicator { filter: invert(.5); }
      `}</style>
    </div>
  );
};

// ─── MetricWithTrend (unchanged, used elsewhere) ──────────────────────────
export const MetricWithTrend = ({ title, value, previousValue, format = (v) => v }) => {
  const growth = previousValue ? ((value - previousValue) / previousValue) * 100 : 0;
  const isPositive = growth >= 0;
  return (
    <div>
      <div style={{ fontSize: 22, fontWeight: 700, color: '#fff' }}>{format(value)}</div>
      {previousValue != null && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 4, marginTop: 4,
          fontSize: 11, color: isPositive ? '#34d399' : '#f87171',
        }}>
          {isPositive ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
          <span>{isPositive ? '+' : ''}{growth.toFixed(1)}%</span>
          <span style={{ color: '#475569', fontSize: 10 }}>vs previous period</span>
        </div>
      )}
    </div>
  );
};